import { useState } from 'react';

import { Button } from '@iwoca/orion';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import Skeleton from 'react-loading-skeleton';

import { MastercardSVG } from './PaymentMethodIcons/MastercardSVG';
import { VisaSVG } from './PaymentMethodIcons/VisaSVG';
import {
  useDeletePaymentMethodCard,
  usePaymentMethodCard,
} from '../../../../../hooks/usePaymentMethod';

export const EditPaymentMethod = () => {
  const { id, lastFourDigits, brand, isLoading } = usePaymentMethodCard();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useDeletePaymentMethodCard();

  const [showRemoveButton, setShowRemoveButton] = useState(false);

  const isFetchingFundingReqs = useIsFetching({
    queryKey: ['fetchGetFundingRequirements'],
  });

  if (isLoading) return <Skeleton />;

  const deleteCard = async () => {
    try {
      await mutateAsync(id);
    } catch (e) {
      // Currently we expect a 404 returned, this ensures we still invalidate queries and update state
      console.error(e);
    }
    await queryClient.invalidateQueries({
      queryKey: ['fetchGetFundingRequirements'],
    });
  };

  const isLoadingDelete = isPending || isFetchingFundingReqs > 0;

  return (
    <div className="flex items-center justify-between">
      {!showRemoveButton ? (
        <div className="flex w-full items-center justify-between xs:flex xs:flex-col xs:items-start">
          <div className="flex h-[76px] items-center justify-center">
            <div className="mr-s">
              <BrandIcon brand={brand || ''} />
            </div>
            <div className="flex flex-col gap-s">
              <p className="m-0 text-s font-med capitalize">{brand}</p>
              <p className="m-0 text-xs">Ending in **** {lastFourDigits}</p>
            </div>
          </div>
          <Button
            variant="primary"
            noShadow={true}
            className="!border-core-40 !bg-core-40 !bg-none xs:mt-l"
            onClick={() => setShowRemoveButton(true)}
          >
            Remove payment method
          </Button>
        </div>
      ) : (
        <div className="flex w-full items-center justify-between xs:flex xs:flex-col xs:items-start">
          <p className="m-0 text-xs">
            Are you sure you want to remove this payment method?
          </p>
          <Button
            variant="primary"
            className="!border-core-40 !bg-core-40 !bg-none xs:mt-l"
            loading={isLoadingDelete}
            onClick={() => deleteCard()}
          >
            Remove
          </Button>
        </div>
      )}
    </div>
  );
};

function BrandIcon({ brand }: { brand: string }) {
  switch (brand) {
    case 'visa':
      return <VisaSVG className="h-[32px]" />;
    case 'mastercard':
      return <MastercardSVG className="h-[32px]" />;
    default:
      return null;
  }
}
